import { Copy, Plus, Trash2 } from 'lucide-react';
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Button } from '@/components/ui/button';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { AccountOption } from '@/features/accounts/api';
import { cn } from '@/lib/utils';

import {
  blankTender,
  incrementChequeNo,
  isCheque,
  PAYMENT_METHODS,
  RECEIPT_METHODS,
  repeatCheque,
  tenderBaseAmount,
  tendersBaseTotal,
  tenderTotals,
  type TenderRow,
} from './tenders';

/** Undeposited Funds — where a cheque sits until it is banked (11.2.1, FIN-10 §12). */
const CHEQUES_IN_HAND_CODE = '1114';

export interface TendersGridProps {
  kind: 'receipt' | 'payment';
  rows: TenderRow[];
  onChange: (rows: TenderRow[]) => void;
  editable: boolean;
  /** The voucher's own currency — a new row is born in it. */
  voucherCurrency: string;
  baseCurrency: string;
  /** The codes a row may be denominated in (`useCurrencyPickerCodes`). */
  currencies: readonly string[];
  /** The postable Cash & Cash Equivalents accounts (BR-FIN-03-04 / its FIN-04 twin). */
  accounts: readonly AccountOption[];
  /** The rate table's figure for a code, for the Rate cell's default; null when none. */
  rateFor: (currency: string) => string | null;
  today: string;
  /** PV-01: the payment editor's endorsed-cheque picker, drawn beside the add buttons. */
  extraActions?: ReactNode;
}

const CELL =
  'h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm outline-none transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-70';
const MONEY_CELL = cn(CELL, 'text-end font-mono tabular-nums');

/**
 * THE TENDERS GRID — S6 (bugs RV-02…RV-06, PV-01): one row per way the money
 * moved. "5,000 ILS cash + 1,000 USD cash + 2,000 by card + 50,000 spread over
 * 10 cheques — ONE document, each cheque with its own due date."
 *
 * Main columns: Method · Currency · Amount · Rate · Base amount · Account ·
 * Reference. A CHEQUE row expands into RV-04's wide entry, in the screenshot's
 * exact order: Cheque no → Bank → Branch → Account no → Amount → Currency →
 * Due date → Rate → Notes → Delete / Repeat — "Repeat" duplicates the row with
 * the cheque number one up and the due date one month on.
 *
 * Footer: a subtotal per currency used and the grand total in the base (RV-06).
 *
 * KEYBOARD — the sales grid's rule (SAL-00 §5d), as the user asked: Enter
 * moves to the next cell in reading order; past the last cell it adds a row
 * and lands on its first cell, so the ten cheques are typed without a mouse.
 *
 * PV-01's endorsed row copies the register's facts and is read-only except the
 * rate and Delete — the facts belong to the cheque, not to this voucher.
 */
export function TendersGrid({
  kind,
  rows,
  onChange,
  editable,
  voucherCurrency,
  baseCurrency,
  currencies,
  accounts,
  rateFor,
  today,
  extraActions,
}: TendersGridProps) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLTableElement | null>(null);
  const methods = kind === 'payment' ? PAYMENT_METHODS : RECEIPT_METHODS;

  const setRow = (key: number, patch: Partial<TenderRow>) => {
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };
  const removeRow = (key: number) => onChange(rows.filter((row) => row.key !== key));

  /** The default rate for a code: the table's, else 1 for the base, else blank for the reader to type. */
  const defaultRate = (currency: string): string =>
    currency === baseCurrency ? '1' : (rateFor(currency) ?? '');

  const chequesInHandId = accounts.find((account) => account.code === CHEQUES_IN_HAND_CODE)?.id ?? '';
  const lastCheque = [...rows].reverse().find((row) => row.method === 'check');

  const addRow = (method: TenderRow['method'] = 'cash'): TenderRow => {
    const previous = rows[rows.length - 1];
    const row = blankTender(voucherCurrency, today, {
      method,
      rate: defaultRate(voucherCurrency),
      accountId:
        method === 'check'
          ? chequesInHandId || (previous?.accountId ?? '')
          : (previous?.accountId ?? ''),
      ...(method === 'check' && lastCheque
        ? {
            chequeNo: incrementChequeNo(lastCheque.chequeNo),
            bankName: lastCheque.bankName,
            branchNo: lastCheque.branchNo,
            accountNo: lastCheque.accountNo,
            dueDate: lastCheque.dueDate || today,
          }
        : {}),
    });
    onChange([...rows, row]);
    return row;
  };

  const onPickMethod = (row: TenderRow, method: TenderRow['method']) => {
    setRow(row.key, {
      method,
      // A cheque lands in the cheques-in-hand pot unless the reader chose otherwise.
      ...(method === 'check' && (!row.accountId || row.accountId === chequesInHandId) && chequesInHandId
        ? { accountId: chequesInHandId }
        : {}),
      // Switching INTO a cheque dates it the day it was written — the voucher's
      // date — whatever the row carried before (nothing meaningful).
      ...(method === 'check' && !isCheque(row) ? { dueDate: today } : {}),
    });
  };

  const onPickCurrency = (row: TenderRow, currency: string) =>
    setRow(row.key, { currency, rate: defaultRate(currency) });

  /** SAL-00 §5d: Enter advances through the grid's cells in reading order. */
  const onGridKeyDown = (event: KeyboardEvent<HTMLTableElement>) => {
    if (event.key !== 'Enter' || !editable) return;
    const target = event.target as HTMLElement;
    if (!target.matches('[data-tender-cell]')) return;
    event.preventDefault();
    const cells = Array.from(
      tableRef.current?.querySelectorAll<HTMLElement>('[data-tender-cell]:not(:disabled)') ?? [],
    );
    const at = cells.indexOf(target);
    const next = cells[at + 1];
    if (next) {
      next.focus();
      return;
    }
    addRow();
    requestAnimationFrame(() => {
      const after = Array.from(
        tableRef.current?.querySelectorAll<HTMLElement>('[data-tender-cell]:not(:disabled)') ?? [],
      );
      after[at + 1]?.focus();
    });
  };

  const totals = tenderTotals(rows);
  const grand = tendersBaseTotal(rows, baseCurrency);

  const accountLabel = (row: TenderRow): string | null => {
    const found = accounts.find((account) => account.id === row.accountId);
    return found ? `${found.code} — ${found.name}` : null;
  };

  return (
    <div className="grid gap-2">
      <Table ref={tableRef} onKeyDown={onGridKeyDown} className="min-w-[960px]">
        <TableHeader>
          <TableRow>
            <TableHead className="w-36">{t('receipts.fMethod')}</TableHead>
            <TableHead className="w-24">{t('tenders.colCurrency')}</TableHead>
            <TableHead className="w-36 text-end">{t('tenders.colAmount')}</TableHead>
            <TableHead className="w-28 text-end">{t('tenders.colRate')}</TableHead>
            <TableHead className="w-36 text-end">
              {t('tenders.colBase', { currency: baseCurrency })}
            </TableHead>
            <TableHead>{t('tenders.colAccount')}</TableHead>
            {/* BR-FIN-03-12 — the label follows the method: a cheque row
                carries its own cheque-no cell below, so this column stays
                the reference for every other method. */}
            <TableHead className="w-40">{t('receipts.fReference')}</TableHead>
            <TableHead className="w-24" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => {
            const line = index + 1;
            const cheque = isCheque(row);
            const endorsed = row.method === 'endorsed_check';
            const base = tenderBaseAmount(row, baseCurrency);
            const held = accountLabel(row);
            return [
              <TableRow key={row.key} className={cn(cheque && 'border-b-0')}>
                <TableCell>
                  {endorsed ? (
                    <span className="text-sm font-medium">{t('receipts.method.endorsed_check')}</span>
                  ) : (
                    <NativeSelect
                      aria-label={t('tenders.methodLine', { n: line })}
                      data-tender-cell
                      className="h-9"
                      value={row.method}
                      onChange={(event) => onPickMethod(row, event.target.value as TenderRow['method'])}
                      disabled={!editable}
                    >
                      {methods
                        .filter((method) => method !== 'endorsed_check')
                        .map((method) => (
                          <option key={method} value={method}>
                            {t(`receipts.method.${method}`)}
                          </option>
                        ))}
                    </NativeSelect>
                  )}
                </TableCell>
                {cheque ? (
                  // The cheque's money cells live on its wide row below; the main
                  // row mirrors them read-only so the column still reads.
                  <>
                    <TableCell className="font-mono text-sm">
                      <Ltr value={row.currency} />
                    </TableCell>
                    <TableCell className="text-end font-mono text-sm tabular-nums">
                      {row.amount ? <Amount value={row.amount} /> : '—'}
                    </TableCell>
                    <TableCell className="text-end font-mono text-sm tabular-nums">
                      <Ltr value={row.rate || '—'} />
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>
                      <NativeSelect
                        aria-label={t('tenders.currencyLine', { n: line })}
                        data-tender-cell
                        className="h-9 font-mono"
                        value={row.currency}
                        onChange={(event) => onPickCurrency(row, event.target.value)}
                        disabled={!editable}
                      >
                        {!currencies.includes(row.currency) && row.currency ? (
                          <option value={row.currency}>{row.currency}</option>
                        ) : null}
                        {currencies.map((code) => (
                          <option key={code} value={code}>
                            {code}
                          </option>
                        ))}
                      </NativeSelect>
                    </TableCell>
                    <TableCell>
                      <input
                        aria-label={t('tenders.amountLine', { n: line })}
                        data-tender-cell
                        dir="ltr"
                        inputMode="decimal"
                        placeholder="0.00"
                        className={MONEY_CELL}
                        value={row.amount}
                        onChange={(event) => setRow(row.key, { amount: event.target.value })}
                        disabled={!editable}
                      />
                    </TableCell>
                    <TableCell>
                      <input
                        aria-label={t('tenders.rateLine', { n: line })}
                        data-tender-cell
                        dir="ltr"
                        inputMode="decimal"
                        className={MONEY_CELL}
                        value={row.rate}
                        onChange={(event) => setRow(row.key, { rate: event.target.value })}
                        disabled={!editable || row.currency === baseCurrency}
                      />
                    </TableCell>
                  </>
                )}
                <TableCell className="text-end font-mono text-sm tabular-nums">
                  {base === null ? '—' : <Amount value={base} />}
                </TableCell>
                <TableCell>
                  {endorsed ? (
                    <span className="text-sm text-muted-foreground">{t('tenders.endorsedAccount')}</span>
                  ) : (
                    <NativeSelect
                      aria-label={t('tenders.accountLine', { n: line })}
                      data-tender-cell
                      className="h-9"
                      value={row.accountId}
                      onChange={(event) => setRow(row.key, { accountId: event.target.value })}
                      disabled={!editable}
                    >
                      <option value="">{t('receipts.pickAccount')}</option>
                      {row.accountId && held === null ? (
                        <option value={row.accountId}>{row.accountId}</option>
                      ) : null}
                      {accounts.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.code} — {option.name}
                        </option>
                      ))}
                    </NativeSelect>
                  )}
                </TableCell>
                <TableCell>
                  {cheque ? (
                    <span className="inline-flex items-center gap-2 font-mono text-sm">
                      {row.chequeCode ? (
                        <Link to={`/banks/checks/${kind === 'payment' && !endorsed ? 'outgoing' : 'incoming'}`} className="text-primary hover:underline">
                          <bdi dir="ltr">{row.chequeCode}</bdi>
                        </Link>
                      ) : (
                        <Ltr value={row.chequeNo || '—'} />
                      )}
                      {/* FIN-10 §6 per tender: THIS cheque bounced and its leg
                          was reversed; the rows beside it still count. */}
                      {row.bouncedAt ? (
                        <span className="rounded-full bg-red-100 px-2 py-0.5 font-sans text-xs font-medium text-red-800 dark:bg-red-950 dark:text-red-300">
                          {t('tenders.bounced')}
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    <input
                      aria-label={t('tenders.referenceLine', { n: line })}
                      data-tender-cell
                      dir="ltr"
                      maxLength={60}
                      className={CELL}
                      value={row.reference}
                      onChange={(event) => setRow(row.key, { reference: event.target.value })}
                      disabled={!editable}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {!cheque && editable ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('tenders.removeLine', { n: line })}
                      title={t('tenders.removeLine', { n: line })}
                      onClick={() => removeRow(row.key)}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>,
              cheque ? (
                <TableRow key={`${row.key}-cheque`} className="bg-muted/30">
                  <TableCell colSpan={8} className="py-2">
                    {/* RV-04's order: Cheque no → Bank → Branch → Account no →
                        Amount → Currency → Due date → Rate → Notes → Delete / Repeat */}
                    <div className="grid gap-2 sm:grid-cols-5 lg:grid-cols-10">
                      <ChequeCell label={t('tenders.chequeNoLine', { n: line })}>
                        <input
                          aria-label={t('tenders.chequeNoLine', { n: line })}
                          data-tender-cell
                          dir="ltr"
                          maxLength={40}
                          className={cn(CELL, 'font-mono')}
                          value={row.chequeNo}
                          onChange={(event) => setRow(row.key, { chequeNo: event.target.value })}
                          disabled={!editable || endorsed}
                        />
                      </ChequeCell>
                      <ChequeCell label={t('tenders.bankLine', { n: line })} className="lg:col-span-2">
                        <input
                          aria-label={t('tenders.bankLine', { n: line })}
                          data-tender-cell
                          maxLength={120}
                          className={CELL}
                          value={row.bankName}
                          onChange={(event) => setRow(row.key, { bankName: event.target.value })}
                          disabled={!editable || endorsed}
                        />
                      </ChequeCell>
                      <ChequeCell label={t('tenders.branchLine', { n: line })}>
                        <input
                          aria-label={t('tenders.branchLine', { n: line })}
                          data-tender-cell
                          dir="ltr"
                          maxLength={40}
                          className={CELL}
                          value={row.branchNo}
                          onChange={(event) => setRow(row.key, { branchNo: event.target.value })}
                          disabled={!editable || endorsed}
                        />
                      </ChequeCell>
                      <ChequeCell label={t('tenders.accountNoLine', { n: line })}>
                        <input
                          aria-label={t('tenders.accountNoLine', { n: line })}
                          data-tender-cell
                          dir="ltr"
                          maxLength={60}
                          className={cn(CELL, 'font-mono')}
                          value={row.accountNo}
                          onChange={(event) => setRow(row.key, { accountNo: event.target.value })}
                          disabled={!editable || endorsed}
                        />
                      </ChequeCell>
                      <ChequeCell label={t('tenders.amountLine', { n: line })}>
                        <input
                          aria-label={t('tenders.amountLine', { n: line })}
                          data-tender-cell
                          dir="ltr"
                          inputMode="decimal"
                          placeholder="0.00"
                          className={MONEY_CELL}
                          value={row.amount}
                          onChange={(event) => setRow(row.key, { amount: event.target.value })}
                          disabled={!editable || endorsed}
                        />
                      </ChequeCell>
                      <ChequeCell label={t('tenders.currencyLine', { n: line })}>
                        <NativeSelect
                          aria-label={t('tenders.currencyLine', { n: line })}
                          data-tender-cell
                          className="h-9 font-mono"
                          value={row.currency}
                          onChange={(event) => onPickCurrency(row, event.target.value)}
                          disabled={!editable || endorsed}
                        >
                          {!currencies.includes(row.currency) && row.currency ? (
                            <option value={row.currency}>{row.currency}</option>
                          ) : null}
                          {currencies.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </NativeSelect>
                      </ChequeCell>
                      <ChequeCell label={t('tenders.dueDateLine', { n: line })}>
                        <input
                          aria-label={t('tenders.dueDateLine', { n: line })}
                          data-tender-cell
                          type="date"
                          dir="ltr"
                          className={CELL}
                          value={row.dueDate}
                          onChange={(event) => setRow(row.key, { dueDate: event.target.value })}
                          disabled={!editable || endorsed}
                        />
                      </ChequeCell>
                      <ChequeCell label={t('tenders.rateLine', { n: line })}>
                        {/* PV-01: the rate is the ONE editable fact on an endorsed cheque. */}
                        <input
                          aria-label={t('tenders.rateLine', { n: line })}
                          data-tender-cell
                          dir="ltr"
                          inputMode="decimal"
                          className={MONEY_CELL}
                          value={row.rate}
                          onChange={(event) => setRow(row.key, { rate: event.target.value })}
                          disabled={!editable || row.currency === baseCurrency}
                        />
                      </ChequeCell>
                      <ChequeCell label={t('tenders.notesLine', { n: line })}>
                        <input
                          aria-label={t('tenders.notesLine', { n: line })}
                          data-tender-cell
                          maxLength={500}
                          className={CELL}
                          value={row.notes}
                          onChange={(event) => setRow(row.key, { notes: event.target.value })}
                          disabled={!editable}
                        />
                      </ChequeCell>
                      {editable ? (
                        <div className="flex items-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={t('tenders.removeLine', { n: line })}
                            title={t('tenders.removeLine', { n: line })}
                            onClick={() => removeRow(row.key)}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                          {!endorsed ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={t('tenders.repeatLine', { n: line })}
                              title={t('tenders.repeatLine', { n: line })}
                              onClick={() => {
                                const copy = repeatCheque(row);
                                const at = rows.findIndex((candidate) => candidate.key === row.key);
                                onChange([...rows.slice(0, at + 1), copy, ...rows.slice(at + 1)]);
                              }}
                            >
                              <Copy aria-hidden />
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ) : null,
            ];
          })}
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                {t('tenders.empty')}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      {/* ── Footer: the add buttons and RV-06's totals ─────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        {editable ? (
          <>
            <Button type="button" variant="outline" size="sm" onClick={() => addRow('cash')}>
              <Plus aria-hidden /> {t('tenders.addTender')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addRow('check')}>
              <Plus aria-hidden /> {t('tenders.addCheque')}
            </Button>
            {extraActions}
          </>
        ) : null}
        <div
          className="ms-auto flex flex-wrap items-center gap-3 font-mono text-sm"
          role="group"
          aria-label={t('tenders.totals')}
        >
          {totals.map((total) => (
            <span key={total.currency} className="rounded-md bg-muted px-2 py-1">
              {t('tenders.subtotal', { currency: total.currency })}{' '}
              <Amount value={total.amount} currency={total.currency} />
            </span>
          ))}
          <span className="rounded-md bg-primary/10 px-2 py-1 font-semibold">
            {t('tenders.grandTotal', { currency: baseCurrency })}{' '}
            <Amount value={grand} currency={baseCurrency} />
          </span>
        </div>
      </div>
    </div>
  );
}

function ChequeCell({
  label,
  className,
  children,
}: {
  label: string;
  className?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className={cn('grid gap-1 text-xs text-muted-foreground', className)}>
      <span>{label}</span>
      {children}
    </label>
  );
}
